import FormTab from '../../../SchedulerPro/widget/taskeditor/FormTab.js';
import '../../../Core/widget/TextField.js';
import { Direction } from '../../../Engine/scheduling/Types.js';
import '../../../SchedulerPro/widget/StartDateField.js';
import '../../../SchedulerPro/widget/EndDateField.js';
import '../../../SchedulerPro/widget/CalendarField.js';
import '../../../Core/widget/RadioGroup.js';
import '../../../Core/widget/Checkbox.js';

/**
 * @module Gantt/widget/projecteditor/ProjectEditorGeneralTab
 */

const scheduleFromOptions = {
    [Direction.Forward]  : 'L{ProjectEditorGeneralTab.forward}',
    [Direction.Backward] : 'L{ProjectEditorGeneralTab.backward}'
};

/**
 * A tab inside the {@link Gantt/widget/ProjectEditor project editor} showing the general information for a project.
 *
 * | Field ref           | Type                                       | Weight | Description                                                                                   |
 * |---------------------|--------------------------------------------|--------|-----------------------------------------------------------------------------------------------|
 * | `nameField`         | {@link Core/widget/TextField}              | 100    | Shows a text field related to {@link Gantt/model/ProjectModel#field-name} field               |
 * | `startDateField`    | {@link SchedulerPro/widget/StartDateField} | 200    | Shows a start date field related to {@link Gantt/model/ProjectModel#field-startDate} field    |
 * | `endDateField`      | {@link SchedulerPro/widget/EndDateField}   | 300    | Shows an end date field related to {@link Gantt/model/ProjectModel#field-endDate} field       |
 * | `calendarField`     | {@link SchedulerPro/widget/CalendarField}  | 400    | Shows a calendar field related to {@link Gantt/model/ProjectModel#field-calendar} field       |
 * | `scheduleFromField` | {@link Core/widget/RadioGroup}             | 500    | Shows a radio group field related to {@link Gantt/model/ProjectModel#field-direction} field   |
 * | `readOnlyField`     | {@link Core/widget/Checkbox}               | 600    | Shows a checkbox group field related to {@link Gantt/model/ProjectModel#field-readOnly} field |
 *
 * @extends SchedulerPro/widget/taskeditor/FormTab
 * @classtype ProjectEditorGeneralTab
 */
export default class ProjectEditorGeneralTab extends FormTab {

    static $name = 'ProjectEditorGeneralTab';

    static type = 'projecteditorgeneraltab';

    static configurable = {
        title : 'L{general}',

        defaults : {
            localeClass : this,
            labelWidth  : '8.5em'
        },

        items : {
            nameField : {
                type   : 'text',
                weight : 100,
                label  : 'L{projectName}',
                name   : 'name',
                cls    : 'b-project-name'
            },
            startDateField : {
                type   : 'startdate',
                weight : 200,
                label  : 'L{start}',
                name   : 'startDate',
                cls    : 'b-start-date'
            },
            endDateField : {
                type   : 'enddate',
                weight : 300,
                label  : 'L{finish}',
                name   : 'endDate',
                cls    : 'b-end-date'
            },
            calendarField : {
                type           : 'calendarField',
                label          : 'L{calendar}',
                icon           : 'b-fa-calendar',
                name           : 'calendar',
                clearable      : false,
                weight         : 400,
                calendarEditor : {
                    centered : true,
                    modal    : true
                }
            },
            scheduleFromField : {
                type    : 'radiogroup',
                label   : 'L{scheduleFrom}',
                name    : 'direction',
                weight  : 500,
                value   : Direction.Forward,  // the default choice
                options : scheduleFromOptions,
                flex    : 1,
                cls     : 'b-schedule-from'
            },
            readOnlyField : {
                type     : 'checkbox',
                weight   : 600,
                name     : 'readOnly',
                label    : 'L{readOnly}',
                cls      : 'b-read-only',
                disabled : false
            }
        }
    };

    afterConstruct() {
        super.afterConstruct(...arguments);

        this.widgetMap.calendarField?.ion({
            beforeEditCalendar : 'up.onBeforeEditCalendar'
        });
    }

    loadEvent(project) {
        const { calendarField } = this.widgetMap;

        if (calendarField) {
            // drop down list will contain all registered calendars
            calendarField.store = project.calendarManagerStore;
            // give the field some context on whose calendar we are editing (needed for calendar editor)
            calendarField.calendarConsumerRecord = project;
        }

        super.loadEvent(...arguments);
    }

    onBeforeEditCalendar({ calendarEditor }) {
        calendarEditor.activeDate = this.widgetMap.startDateField.value;
    }
}

// Register this widget type with its Factory
ProjectEditorGeneralTab.initClass();
